// components/sections/contact/ContactChannelItem.tsx
"use client";

import { useCallback, useRef, useState } from "react";
import { Check, Copy, ArrowUpRight } from "lucide-react";
import clsx from "clsx";
import type { ContactChannel } from "@/data/contact-page";

interface ContactChannelItemProps {
  channel: ContactChannel;
  index: number;
}

function HexBadge({ index }: { index: number }) {
  const labels = ["A", "B", "C"];
  return (
    <div className="relative flex h-10 w-10 shrink-0 items-center justify-center">
      <svg viewBox="0 0 40 40" className="absolute inset-0 h-full w-full">
        <path
          d="M20 2 L36 11 L36 29 L20 38 L4 29 L4 11 Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="text-amber-300"
        />
      </svg>
      <span className="relative text-[11px] font-bold text-amber-700">
        {labels[index] ?? String(index + 1)}
      </span>
    </div>
  );
}

export function ContactChannelItem({ channel, index }: ContactChannelItemProps) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(channel.email);
      setCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable
    }
  }, [channel.email]);

  return (
    <li className="group flex items-start gap-4 rounded-2xl border border-transparent bg-transparent p-4 -mx-4 transition-all duration-300 hover:border-amber-200/80 hover:bg-amber-50/50">
      <HexBadge index={index} />

      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-[2.5px] text-stone-400">
          {channel.label}
        </p>

        <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <a
            href={`mailto:${channel.email}`}
            className="inline-flex items-center gap-1 text-[15px] font-semibold text-stone-800 hover:text-amber-700 transition-colors duration-200 break-all"
          >
            {channel.email}
            <ArrowUpRight
              size={13}
              className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            />
          </a>

          <button
            type="button"
            onClick={handleCopy}
            aria-label={copied ? "Email copied" : `Copy ${channel.email}`}
            className={clsx(
              "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[1.5px] transition-all duration-200",
              copied
                ? "bg-green-100 text-green-700"
                : "bg-stone-100 text-stone-500 hover:bg-amber-100 hover:text-amber-700"
            )}
          >
            {copied ? (
              <>
                <Check size={10} strokeWidth={3} />
                Copied
              </>
            ) : (
              <>
                <Copy size={10} strokeWidth={2.5} />
                Copy
              </>
            )}
          </button>
        </div>

        <p className="mt-1 text-[13px] leading-relaxed text-stone-500">
          {channel.description}
        </p>
      </div>
    </li>
  );
}